import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { PdfViewerComponent } from "@shared/components/pdf-viewer/pdf-viewer.component";
import { BoundingBox, CatDoc, IndPhrase, ReferenceDocument } from "@shared/models/cat.model";
import { AnchorResult, BoundingBoxAnchorResult, ClaimReferenceAnchorResult, ReferencePhrase, UserAddedAnchorResult } from "@shared/models/common";
import { EmbeddedObjectAnnotation } from "@shared/models/embedded-object-annotation";
import { InfoMessageInitializer } from "@shared/models/infomessage-initializer";
import { RevDoc, RevRule } from "@shared/models/rev.model";
import { CatService } from "@shared/services/cat.service";
import { IntegrationService } from "@shared/services/integration.service";
import { SharedService } from "@shared/services/shared.service";
import { UtilityService } from "@shared/services/utility.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ClaimReferenceDetails } from "../claims-view/model/claim-reference-details";


@Component({
    selector: 'add-anchor',
    templateUrl: './add-anchor.component.html',
    styleUrls: ['./add-anchor.component.scss'],
    standalone: false
})
export class AddAnchorComponent implements OnDestroy, OnChanges {

selectedRefDocId: number | null = null;
@Input() selectedRefDoc: ReferenceDocument | null = null;
selectedAnchor: AnchorResult | null = null;
refDocs: ReferenceDocument[] = [];
  selectedAnchorId: number | null = null;


@Input() curDoc!: CatDoc | RevDoc | {catVersionId: number};
@Input() mode: 'add-anchor' | 'claim-form' = 'add-anchor';

// used 'add-anchor' mode
@Input() reference: ReferencePhrase | undefined;

// should be used only in 'claim-form' mode 
@Input() claimReference: ClaimReferenceDetails | undefined;

@Input() showAddContextDropdown: {value: boolean} ={
  value: false
};
@Input() layoutClass: string = '';
@Input() phrase!: IndPhrase | RevRule;
@Input() disabled: boolean = false;

displayErrorMessage: boolean = false;
displayAddingAnchors:boolean = false;
displayFormForCreatingBoundingBoxAnchor: boolean = false;
anchorToCreate: BoundingBoxAnchorResult | null = null;

@Output() addedAnchor = new EventEmitter();

destroy$ = new Subject<void>();
@ViewChild('anchorPanel', { read: ElementRef }) anchorPanel?: ElementRef;
anchorPathValue: string | undefined = undefined;
selectDisplayValue: string | null = null;
displayPdfForDrawingAnchors = false;

@ViewChild('docViewer') docViewer: PdfViewerComponent;

constructor(private integrationService: IntegrationService, private catService: CatService, private sharedService: SharedService, private utilityService: UtilityService, private translateService: TranslateService) { }


ngOnChanges(changes: SimpleChanges): void {
        
        if (changes['showAddContextDropdown']) {

            this.initContextDropDown();
        }
    }

ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
    


initContextDropDown() {

  this.selectedAnchor = null;
    if (this.mode == 'claim-form') {

      this.initContextDropDownCreateClaim();
      return;

    }
    this.selectedRefDocId = null;
      this.selectedRefDoc = null;
      this.selectedAnchor = null;
      //this.showAddContextDropdown = !this.showAddContextDropdown;
      if (!this.reference) {
        this.displayAddingAnchors = true;
        setTimeout(() => this.anchorPanel?.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        }));

      }
      if (this.showAddContextDropdown.value && this.curDoc?.catVersionId) {
        this.integrationService.getAvailableReferenceDocs(this.curDoc.catVersionId)
            .pipe(takeUntil(this.destroy$))
            .subscribe((docs: ReferenceDocument[]) => {
              if (!this.reference) {
              const refTexts: string[] = [];

              if (this.phrase?.references) {
                for (const ref of this.phrase.references) {
                  if (ref?.refCatMatchTxt) {
                    refTexts.push(ref.refCatMatchTxt.trim());
                  } else if (ref?.refPhraseText) {
                    refTexts.push(ref.refPhraseText.trim());
                  }
                }
              }

              if (refTexts.length > 0) {
                this.refDocs = docs.filter(doc => {
                  const cleanMappedTexts = (doc.mappedReferenceTexts || [])
                    .map(m => m?.trim())
                    .filter(m => !!m);

                  if (cleanMappedTexts.length > 0) {
                    const hasFullMatch = refTexts.some(refText =>
                      cleanMappedTexts.every(m => m === refText)
                    );
                    return !hasFullMatch;
                  } else if (doc.docName) {
                    const cleanDocName = doc.docName.trim();
                    const isDocNameInRefTexts = refTexts.some(refText => refText === cleanDocName);
                    return !isDocNameInRefTexts;
                  }
                  return true;
                });
              } else {
                this.refDocs = docs;
              }
            } else  {

              this.refDocs = docs;
                // for each document check if the mappedReferenceTexts contains the reference.refCatMatchTxt
                for (const doc of this.refDocs) {
                  if (doc.mappedReferenceTexts?.some(m => m.trim() === this.reference?.refCatMatchTxt?.trim() || m.trim() === this.reference?.refPhraseText?.trim()) || doc.docName?.trim() === this.reference?.refPhraseText?.trim()) {
                    this.selectedRefDoc = doc;
                    this.selectedRefDocId = doc.docId;
                    break;
                  }
              }
              if (this.selectedRefDoc) {
                this.displayAddingAnchors = true;
                
                this.refDocs = [this.selectedRefDoc];
                this.onRefDocSelect({ value: this.selectedRefDoc.docId });
              } else {
                this.displayErrorMessage = true;
              }
            }

          
            });
    }
  }

  initContextDropDownCreateClaim() {

    if (this.showAddContextDropdown.value && this.selectedRefDoc) {
      this.displayAddingAnchors = true;
      this.selectedRefDocId = this.selectedRefDoc.docId;

      this.refDocs = [this.selectedRefDoc];
      this.onRefDocSelect({ value: this.selectedRefDoc.docId });
    } else {
      this.displayErrorMessage = true;
    }
  }



  onRefDocSelect(event: any) {
    const docId = event.value;
    this.selectedAnchor = null;
    this.selectedAnchorId = null;
    if (!docId) {
      this.selectedRefDoc = null;
      return;
    }
    const doc = this.refDocs.find(d => d.docId === docId);
    if (doc) {
      this.selectedRefDoc = doc;
        this.integrationService.getAnchorsForDoc(doc.docId, this.curDoc?.catVersionId)
          .pipe(takeUntil(this.destroy$))
          .subscribe((anchors: any[]) => {

            doc.anchors = [];
            let ref = this.mode == 'claim-form' ? this.claimReference : this.reference;

            for (const a of anchors) {
              
              let foundAnchor = ref?.anchors?.find(consistencyCheckResult => consistencyCheckResult.refDocAnchorId == a.refDocAnchorId && consistencyCheckResult.refDocId == doc.docId);
              if (!foundAnchor) {

                let embeddedObjectAnnotation = this.catService.mapToEmbeddedObjectAnnotation(a.embeddedObjectAnnotation);
                if (a.path != undefined) {
                  embeddedObjectAnnotation.anchorPath = a.path;
                }
                doc.anchors.push(new UserAddedAnchorResult(
                  a.phrases?.[0]?.anchorPhraseId || a.refDocAnchorId,
                  a.refDocAnchorId,
                  this.selectedRefDoc.docId || 0,
                  embeddedObjectAnnotation
                  
                  
                  ))
              }
            }
          });
      }
  }

   getSelectDisplayValue(): string {
    
    if (this.selectedAnchor) {
      // Resolves your fallback rule: property1 || property2
      return this.selectedAnchor.embeddedObjectAnnotation.anchorPath || this.selectedAnchor.embeddedObjectAnnotation.startingPhrase || "";
    }
    return this.anchorPathValue ?? "";
  }

  setSelectDisplayValue(id: number | string) {

    this.onAnchorSelect({value: id});
  }
  onAnchorSelect(event: any) {
    const anchorId = event.value;
    if (!anchorId) {
      
      this.selectedAnchor = null;
      this.anchorPathValue = undefined;

      return;
    }
    this.selectedAnchor = this.selectedRefDoc?.anchors?.find(a => a.refDocAnchorId === anchorId) || null;

    if (this.selectedAnchor == null) {
      
      this.anchorPathValue = anchorId;
    } else {
      this.anchorPathValue = undefined;
    }
    this.selectDisplayValue = this.getSelectDisplayValue();
  }

  addSelectedRefDoc() {
    
    if (!this.selectedRefDoc || !this.selectedAnchor) {
      return;

    }

    if (this.mode == 'claim-form') {
      
      this.addSelectedRefDocCreateClaim(this.selectedAnchor);
      return;
    }


    this.createReferenceAndAddAnchor(this.selectedAnchor);


    this.phrase.consistencyIssuesToUpdate.add.phrase_reference_consistency_check_results.push({
      issue_id: null,
      ref_doc_id: this.selectedRefDoc.docId,
      ref_doc_anchor_id: this.selectedAnchor.refDocAnchorId
    });
    this.addedAnchor.emit();


    
    this.selectedRefDocId = null;
    this.selectedRefDoc = null;
    this.selectedAnchor = null;
    this.displayAddingAnchors = false;
    this.showAddContextDropdown.value = false;
  }
  addSelectedRefDocCreateClaim(anchorToAdd: AnchorResult) {
    if (!this.claimReference || this.mode != 'claim-form') {
      return;
    }
      if (!this.selectedRefDoc || !anchorToAdd) {
      return;

    }
    let claimReferenceAnchor = anchorToAdd;
    if (!(claimReferenceAnchor instanceof BoundingBoxAnchorResult)) {
        
      claimReferenceAnchor = new ClaimReferenceAnchorResult(anchorToAdd.issueId, anchorToAdd.refDocAnchorId, anchorToAdd.refDocId, anchorToAdd.embeddedObjectAnnotation);
    }
    if (!this.claimReference.anchors) {
      
      this.claimReference.anchors = [];
    }
    this.claimReference.anchors.push(claimReferenceAnchor);


    this.selectedAnchor = null;
    this.displayAddingAnchors = false;
    this.showAddContextDropdown.value = false;
  }
  //currently not used
  //previously used to add anchor directly
  addUserAnchor() {
      if (this.selectedRefDoc == null || this.reference == null) {
        
        return;
      }
      const embeddedAnnotation = new EmbeddedObjectAnnotation();
      embeddedAnnotation.docName = this.selectedRefDoc.docName;
      embeddedAnnotation.docId = this.selectedRefDoc.docId;
      embeddedAnnotation.anchorPath = this.anchorPathValue;

      const result = new UserAddedAnchorResult(this.utilityService.getAnchorClientId(), null, this.selectedRefDoc.docId, embeddedAnnotation);


      if (!this.reference.anchors) {
        this.reference.anchors = [];
      }
      this.reference.anchors.push(result);  
      this.phrase.consistencyIssuesToUpdate.add.phrase_reference_consistency_check_results.push({
            issue_id: null,
            ref_doc_id: this.selectedRefDoc.docId,
            ref_doc_anchor_id: null,
            ref_doc_anchor_path: this.anchorPathValue
        });
      this.anchorPathValue = undefined;
      this.addedAnchor.emit();
}

boundingBoxDraw(box: BoundingBox) {
  

      // display confirmation dialog
      if (this.docViewer == undefined || this.selectedRefDoc == undefined) {
        return;
      }



          // sendRequest
          // close dialog
          
          console.log(this.docViewer.currentPdfPage);
          this.integrationService.resolveBoundingBoxAnchors(box, this.docViewer.displayedPage-1, this.docViewer.doc.docId, this.curDoc.catVersionId).subscribe({
            next: (resolvedAnchor) => {
            
            const embeddedObjectAnnotation = this.catService.mapToEmbeddedObjectAnnotation(resolvedAnchor.embeddedObjectAnnotation);
            // get all phrase ids
           const phraseIds: number[] =  this.catService.getEmbeddedObjectPhraseIds(resolvedAnchor.embeddedObjectAnnotation);
           const imageInfo = this.catService.getEmbeddedObjectImageInfo(resolvedAnchor.embeddedObjectAnnotation);
            const existingAnchorId = (resolvedAnchor as any).ref_doc_anchor_id ?? (resolvedAnchor as any).refDocAnchorId ?? null;

            let anchor = new BoundingBoxAnchorResult(this.utilityService.getAnchorClientId(), existingAnchorId, this.selectedRefDoc.docId, embeddedObjectAnnotation);
            anchor.phraseIds = phraseIds;
            anchor.imagesInfos = imageInfo;
            const resolvedPath = (resolvedAnchor as any).path ?? (resolvedAnchor as any).anchorPath;
            if (resolvedPath != undefined) {
              anchor.embeddedObjectAnnotation.anchorPath = resolvedPath;
              anchor.embeddedObjectAnnotation.originalAnchorPath = resolvedPath;
            }
            this.docViewer.removeBoundingBoxes();
            let annotations = embeddedObjectAnnotation.annotationPhrases[embeddedObjectAnnotation.pageNum]
            if (annotations?.length > 1) {

              annotations.forEach(phrase => {
                this.docViewer.showAnnotation(phrase, true);
              });
            } else {
            this.docViewer.showAnnotation(anchor.embeddedObjectAnnotation.boundingBox, true);
            }
            this.displayFormForCreatingBoundingBoxAnchor = true;
            this.anchorToCreate = anchor;


            },
          error: (err) => {
          let messageKey = '';
          if (err?.error?.code == 400) {

            let errDescr = err.error.description;
            messageKey = 'translate.general.' + errDescr.toLowerCase().replace(/\s+/g, "-");

          }
          
          if (!messageKey) {
            messageKey = 'translate.anchors.error-anchor-resolving.message';
          }
          const infoInitializer: InfoMessageInitializer = {            
            infoType: 'error',
            messageKey: messageKey,
            titleKey: 'translate.anchors.anchor-error.title'

          } 
          this.sharedService.showInfoMessage(infoInitializer);
          
        }
          
        
          
        })
        
      ;
    }

    createReferenceAndAddAnchor(anchor: AnchorResult) {

          if (this.selectedRefDoc == null) {
            return;
          }

          
          let ref = this.reference
          if (!ref) {
            ref = new ReferencePhrase();
            ref.newSw = true;
            ref.refType = 'REFERENCE';
            ref.phraseId = 0;
            ref.showInTile = true;
            ref.showAnchors = true;
            ref.isConsistencyGenerated = true;
            ref.refPhraseId = anchor.refDocAnchorId || this.selectedRefDoc?.docId;
            ref.refDocId = this.selectedRefDoc.docId;
            ref.refPhraseText = this.selectedRefDoc.docName;
            if (this.phrase.references == null) {
              
              this.phrase.references = [];
            }
            this.phrase.references.push(ref);
            //ref.saveOriginal();
          }

          if (!ref.anchors) {
            ref.anchors = [];
          }
          ref.anchors.push(anchor);
          // check if added reference exist as referenceIssue in cat match
          let refIssues = this.phrase.referenceIssues?.filter((issue) => issue.refDocId === this.selectedRefDoc?.docId && issue.refFinding === 'MISSING_REFERENCE');
          if (refIssues && refIssues.length > 0) {
                refIssues.forEach((refIssue) => {
                  refIssue.refPhraseId = ref.refPhraseId;
                  refIssue.refFinding = 'COPIED_ANCHOR_REFERENCE';            
                });
                this.phrase.consistencyIssuesToUpdate.is_ref_consistent_anchor_update_only = false;
          }
          // check if added reference exist as consistency generated reference in cat match
          let index = this.phrase.references?.findIndex((r) => r.refDocId === this.selectedRefDoc?.docId && r.isConsistencyGenerated);
          if (index != -1) {
            this.phrase.consistencyIssuesToUpdate.is_ref_consistent_anchor_update_only = false;

          }
    }
  drawAnchorBoundingBox() {

    this.displayPdfForDrawingAnchors = true;
  }
  closePdfForAddingBoundingBoxAnchor() {
    this.displayPdfForDrawingAnchors = false; 
  }
  disableDrawModeOnPageChange() {
    
    this.docViewer.drawMode = false;
    this.docViewer.docToolbar.toggleDrawModeSw = false;
  }
  createBoundingBoxAnchor() {
    if (!this.anchorToCreate) {
      return;
    }
    // has to be refactored
    if (this.mode == 'claim-form') {

      this.addSelectedRefDocCreateClaim(this.anchorToCreate);
      return;
    }
    if (this.anchorToCreate.refDocAnchorId != null) {
      this.createReferenceAndAddAnchor(this.anchorToCreate);
      this.phrase.consistencyIssuesToUpdate.add.phrase_reference_consistency_check_results.push({
        issue_id: null,
        ref_doc_id: this.anchorToCreate.refDocId,
        ref_doc_anchor_id: this.anchorToCreate.refDocAnchorId
      });
    } else {
      this.phrase.boundingBoxAnchorsToAdd.push(this.anchorToCreate);
      this.createReferenceAndAddAnchor(this.anchorToCreate);
    }
    this.displayPdfForDrawingAnchors = false;

    this.displayAddingAnchors = false;
    this.showAddContextDropdown.value = false;
    this.anchorToCreate = null;
    this.displayFormForCreatingBoundingBoxAnchor = false;
    
    this.addedAnchor.emit();

  }
  closeCreateBoundingBoxAnchorForm() {
    if (!this.anchorToCreate) {
      return;
    }
    this.docViewer.hideAnnotation(this.anchorToCreate.embeddedObjectAnnotation.boundingBox);
    this.anchorToCreate = null;
    this.displayFormForCreatingBoundingBoxAnchor = false;
    

  }
}